import type { LHCDetails } from '@diamond/shared/types';
import { useAuthStore } from '@diamond/sol-admin/features/auth';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  SearchLHCItemMutationsParams,
  UpdateLHCItemRemarkPayload,
  UpdateLHCItemRemarkResponse,
} from '../api';
import {
  addLHCItems,
  allocateLHCItemMutations,
  createLHC,
  deleteLHC,
  deleteLHCItem,
  duplicateLHC,
  getCollectionMutationDetail,
  getCollectionPaymentProofDetail,
  getRelatedDeliveryOrders,
  reorderLHCItem,
  replaceLHCItemMutations,
  searchLHCDeliveryOrders,
  searchLHCItemMutations,
  updateLHC,
  updateLHCItemRemark,
} from '../api';
import { COLLECTION_QUERY_KEYS } from '../api/constants';
import type {
  CreateLHCPayload,
  DuplicateLHCPayload,
  UpdateLHCPayload,
} from '../types';

const USE_MOCK_SEARCH_MUTATIONS =
  process.env.NEXT_PUBLIC_USE_MOCK_SEARCH_MUTATIONS === 'true';

export function useCreateLHCMutation(options?: {
  onSuccess?: (data: LHCDetails) => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateLHCPayload) =>
      createLHC(session.access_token, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdateLHCMutation(options?: {
  onSuccess?: (data: LHCDetails) => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateLHCPayload }) =>
      updateLHC(session.access_token, id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useSearchLHCDeliveryOrdersMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      lhcId,
      searchQuery,
    }: {
      lhcId: string;
      searchQuery: string;
    }) => searchLHCDeliveryOrders(session.access_token, lhcId, searchQuery),
    onError: options?.onError,
  });
}

export function useSearchLHCItemMutationsMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      params,
    }: {
      lhcId: string;
      itemId: string;
      params: SearchLHCItemMutationsParams;
    }) => {
      if (USE_MOCK_SEARCH_MUTATIONS) {
        return import('@diamond/sol-admin/utils').then(
          ({ mockSearchLHCItemMutations }) =>
            mockSearchLHCItemMutations(
              session.access_token,
              lhcId,
              itemId,
              params
            )
        );
      }
      return searchLHCItemMutations(
        session.access_token,
        lhcId,
        itemId,
        params
      );
    },
    onError: options?.onError,
  });
}

export function useCollectionPaymentProofDetailMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      paymentProofId,
      orderMetaId,
    }: {
      paymentProofId: string;
      orderMetaId: string;
    }) =>
      getCollectionPaymentProofDetail(
        session.access_token,
        paymentProofId,
        orderMetaId
      ),
    onError: options?.onError,
  });
}

export function useCollectionMutationDetailMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      mutationId,
      orderMetaId,
    }: {
      mutationId: string;
      orderMetaId: string;
    }) =>
      getCollectionMutationDetail(
        session.access_token,
        mutationId,
        orderMetaId
      ),
    onError: options?.onError,
  });
}

export function useAllocateLHCItemMutationsMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      allocations,
    }: {
      lhcId: string;
      itemId: string;
      allocations: Array<{ bank_mutation_id: string; amount_applied: number }>;
    }) =>
      allocateLHCItemMutations(session.access_token, lhcId, itemId, {
        allocations,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

export function useReplaceLHCItemMutationsMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      allocations,
    }: {
      lhcId: string;
      itemId: string;
      allocations: Array<{ bank_mutation_id: string; amount_applied: number }>;
    }) =>
      replaceLHCItemMutations(session.access_token, lhcId, itemId, {
        mode: 'replace',
        allocations,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

export function useAddLHCItemsMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      deliveryOrderIds,
    }: {
      lhcId: string;
      deliveryOrderIds: string[];
    }) => addLHCItems(session.access_token, lhcId, deliveryOrderIds),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

export function useGetRelatedDeliveryOrdersMutation(options?: {
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();

  return useMutation({
    mutationFn: ({
      lhcId,
      poNumber,
      excludeDeliveryOrderId,
    }: {
      lhcId: string;
      poNumber: string;
      excludeDeliveryOrderId?: string;
    }) =>
      getRelatedDeliveryOrders(
        session.access_token,
        lhcId,
        poNumber,
        excludeDeliveryOrderId
      ),
    onError: options?.onError,
  });
}

export function useReorderLHCItemMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      index,
    }: {
      lhcId: string;
      itemId: string;
      index: number;
    }) => reorderLHCItem(session.access_token, lhcId, itemId, index),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

export function useDeleteLHCItemMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ lhcId, itemId }: { lhcId: string; itemId: string }) =>
      deleteLHCItem(session.access_token, lhcId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

export function useDuplicateLHCMutation(options?: {
  onSuccess?: (data: LHCDetails) => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: string;
      payload: DuplicateLHCPayload;
    }) => duplicateLHC(session.access_token, id, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeleteLHCMutation(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteLHC(session.access_token, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_LIST],
      });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

export function useUpdateLHCItemRemarkMutation(options?: {
  onSuccess?: (data: UpdateLHCItemRemarkResponse) => void;
  onError?: (error: Error) => void;
}) {
  const session = useAuthStore();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      lhcId,
      itemId,
      payload,
    }: {
      lhcId: string;
      itemId: string;
      payload: UpdateLHCItemRemarkPayload;
    }) => updateLHCItemRemark(session.access_token, lhcId, itemId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [COLLECTION_QUERY_KEYS.LHC_DETAIL],
      });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
